import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AlertListQuery,
  CreateReportInput,
  RaiseAlertInput,
  ReportListQuery,
} from '@teentack/shared';
import { createReport, fetchMyAlerts, fetchMyReports, raiseAlert } from './safety.api';

export const safetyKeys = {
  all: ['safety'] as const,
  reports: (query: Partial<ReportListQuery>) => ['safety', 'reports', query] as const,
  alerts: (query: Partial<AlertListQuery>) => ['safety', 'alerts', query] as const,
};

export function useMyReports(query: Partial<ReportListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: safetyKeys.reports(query),
    queryFn: () => fetchMyReports(query),
    enabled,
  });
}

export function useMyAlerts(query: Partial<AlertListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: safetyKeys.alerts(query),
    queryFn: () => fetchMyAlerts(query),
    enabled,
  });
}

export function useCreateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReportInput) => createReport(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: safetyKeys.all });
    },
  });
}

/**
 * Raising an alarm.
 *
 * No optimistic update and no retry logic: someone pressing this needs to know
 * whether it actually went through, not to be reassured by an interface.
 */
export function useRaiseAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RaiseAlertInput) => raiseAlert(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: safetyKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
